import { Component } from 'vue-property-decorator';
import MenuBase from '@/views/layout/navigation/menu-base';
import UrlMixin, { ExcludeTypeEnum } from '@/components/query/url-mixin';
import { AclMixin } from '@/components/acl/acl-mixin';
import { mixins } from 'vue-class-component';
import { camelCase } from 'lodash';
import MenuReportDialogsMixin from '@/views/layout/navigation/mixins/menu-report-dialogs-mixin';
import tutorial from '@/components/tutorial/tutorial';
import { DrillFilterOptionsEnum } from '@/services/models/Enums';
import bus from '@/services/evbus';
import UserApi from '@/services/rest/bemob/user';

export interface MenuReportRowAction {
  key: string;
  icon?: string;
  title?: string;
  disabled?:  boolean;
  showCondition?: boolean;
  click?: (event?: any) => void;
}

@Component
export default class MenuReportActionsMixin extends mixins(MenuReportDialogsMixin, MenuBase, UrlMixin, AclMixin) {
  userApi: UserApi = new UserApi();
  get editDrilldownAction() {
    const { drilldown } = this.$store.state.table; 
    const lastDrill: any = drilldown?.[drilldown.length - 1] || {};
    const normalizedGroup = this.normalizeGroup(lastDrill.filterTables);
    const drillCanBeEdited = lastDrill 
      && lastDrill.filterOptions === DrillFilterOptionsEnum.equals
      && lastDrill.children.length === 0;

    return {
      key: 'editDrilldown_'+this.$route.params.type,
      icon: 'far fa-pen',
      title: drillCanBeEdited && this.editDrilldownActionText(normalizedGroup),
      showCondition: drillCanBeEdited && this.dialogActionsEvents[normalizedGroup]?.edit,
      disabled: !this.aclCanViewItem({
        id: lastDrill.filterValues, 
        workspaceId: lastDrill.workspaceId
      }),
      click: () => this.dialogActionsEvents[normalizedGroup]?.edit(lastDrill.filterValues)
    }
  }

  get isMultipleEdit() {
    const selectedItems = this.$store.state.table.selectedItems.filter(({ isDirect }) => !isDirect)
    return selectedItems.length > 1
  }

  get isBulkEdit() {
    return ['campaigns', 'offers', 'landings'].includes(this.selectedGroup) && this.isMultipleEdit
  }
  
  get isAnySelectedItemsNotElements(){
    const { selectedItems } = this.$store.state.table;
    return selectedItems.some((item)=>![
      'campaigns-name',
      'offers-name',
      'landings-name',
      'flows-name',
      'traffic_sources-name',
      'affiliate_networks-name'
    ].includes(item.grouping));
  }

  get itemActions() {
    const { selectedItems } = this.$store.state.table;
    return {
      edit: {
        key: `edit_${this.$route.params.type}`,
        icon: 'far fa-pen',
        title: this.isBulkEdit ? this.t('Bulk Edit') : this.t('Edit'),
        planLimits: this.bulkEditPlanLimits,
        showCondition: !!this.dialogActionsEvents[this.selectedGroupNormalized]?.edit,
        disabled: !this.bulkEditDisallowed,
        click: () => {
          const action = selectedItems.length > 1 ? 'bulkEdit' : 'edit'
          return this.dialogActionsEvents[this.selectedGroupNormalized][action]?.()
        }
      },
      editDrilldown: this.editDrilldownAction,
      create: {
        key: 'create_'+this.$route.params.type,
        icon: 'far fa-plus',
        title: this.t('New'),
        planLimits: this.creationPlanLimits,
        showCondition:  !this.isReport() && !!this.dialogActionsEvents[this.selectedGroupNormalized]?.create,
        disabled: this.createDisallowed || (this.is('campaigns') && this.$store.getters.allPrivateWorkspaces.length === 0),
        click: () => {
          this.dialogActionsEvents[this.selectedGroupNormalized].create();
          this.tutorial.modal();
        }
      },
      createCampaign: {
        key: 'createCampaign_'+this.$route.params.type,
        icon: 'far fa-plus',
        title: this.t('New Campaign'),
        planLimits: 'campaignsAmount',
        showCondition: !this.isReport() 
          && !this.is('campaigns')
          && this.dialogActionsEvents[this.selectedGroupNormalized]?.create,
        disabled: this.createDisallowed || (this.is('campaigns') && this.$store.getters.allPrivateWorkspaces.length===0),
        click: () => {
          this.dialogActionsEvents.campaigns.create();
          this.tutorial.modal();
        }
      },
      createFromTemplate: {
        key: 'createFromTemplate_'+this.$route.params.type,
        icon: 'far fa-plus',
        planLimits: this.createFromTemplatePlanLimits,
        title: this.t('New From Template'),
        showCondition: !this.isReport() && (this.is('affiliate-networks') || this.is('traffic-sources')),
        disabled: this.createDisallowed,
        click: () => this.dialogActionsEvents.common.createFromTemplate()
      },
      drilldownHome: {
        key: 'report_'+this.$route.params.type,
        title: this.t('Report'),
        icon: 'far fa-random',
        click: this.report,
        showCondition: !this.isReport(),
        disabled: !selectedItems.length,//!this.$store.state.tableNextReport.id
        planLimits: this.bulkReportPlanLimits,
      },
      drilldown: {
        key: 'drilldown_'+this.$route.params.type,
        title: this.t('Drilldown'),
        icon: 'far fa-random',
        click: this.handleDrilldownForward,
        showCondition: this.isReport(),
        disabled: !selectedItems.length,//!this.$store.state.tableNextReport.id
        planLimits: this.bulkReportPlanLimits,
      },
      drilldownContinueNewTab: {
        key: 'drilldown_'+this.$route.params.type,
        title: this.t('Drilldown with new tab'),
        icon: 'far fa-random',
        click: this.handleDrilldownForwardNewTab,
        showCondition: this.isReport(),
        disabled: !selectedItems.length,//!this.$store.state.tableNextReport.id
        planLimits: this.bulkReportPlanLimits,
      },
      drilldownOpenInNewTab: {
        key: 'drilldownOpenInNewTab_'+this.$route.params.type,
        title: this.t('Report with new tab'),
        icon: 'far fa-random',
        click: this.report,
        disabled: !selectedItems.length,//!this.$store.state.tableNextReport.id
        planLimits: this.bulkReportPlanLimits,
      },
      campaignLinks: {
        key: 'campaignLinks_'+this.$route.params.type,
        icon: 'far fa-link',
        title: this.t('Campaign links'),
        showCondition: this.is('campaigns'),
        disabled: !this.viewDisallowed,
        click: () => this.dialogActionsEvents.campaigns.campaignLinks()
      },
      startStop: {
        key: 'startStop_'+this.$route.params.type,
        icon: `far fa-${this.startStopButtonIcon}`,
        title: this.startStopButtonText,
        showCondition: this.is('campaigns') && !!this.$store.getters.selectedItem?.['DT_RowCampaignExternalStatus'] && this.$store.getters.selectedItem?.['DT_RowIntegrationType'] === 'propeller',
        disabled: ['3','9'].includes(this.$store.getters.selectedItem?.['DT_RowCampaignExternalStatus']) || this.$store.getters.selectedItem?.['DT_RowIntegrationStatus'] !== 'active',
        planLimits: this.startStopPlanLimits,
        click: () => this.dialogActionsEvents.campaigns.startStop({
          id:this.$store.getters.selectedItem.id,
          buttonText:this.startStopButtonText
        }).catch((error)=>bus.emit('toast-error', `${this.t('This action can’t be done.')}  ${error?.response?.data?.errors?.status?.[0]?.message}`))
      },
      updateCost: {
        key: 'updateCost_'+this.$route.params.type,
        icon: 'far fa-funnel-dollar',
        title: this.t('Update Cost'),
        showCondition: !this.isReport() && this.is('campaigns') && this.isUserOrMemberAdmin,
        disabled: !this.nextReport.id && !this.editDisallowed,
        click: () => this.dialogActionsEvents.campaigns.updateCost()
      },
      duplicate: {
        key: 'duplicate_'+this.$route.params.type,
        icon: 'far fa-copy',
        title: this.t('Duplicate'),
        showCondition: this.isMainTab && this.dialogActionsEvents[this.selectedGroupNormalized]?.duplicate,
        disabled: this.duplicateDisallowed,
        click: () => this.dialogActionsEvents[this.selectedGroupNormalized].duplicate()
      },
      archive: {
        key: 'archive_'+this.$route.params.type,
        icon: 'far fa-archive',
        title: this.t('Archive'),
        showCondition: !this.isReport() && !this.archiveDisallowed,
        disabled: !this.toArchive().length || !this.isUserOrMemberAdmin,
        click: () => this.dialogActionsEvents.common.archive()
      },
      restore: {
        key: 'restore_'+this.$route.params.type,
        icon: 'far fa-undo',
        title: this.t('Restore'),
        showCondition: !this.isReport() && this.toRestore().length,
        disabled: !this.toRestore().length || !this.isUserOrMemberAdmin,
        click: () => this.dialogActionsEvents.common.restore()
      },
      copyUrl: {
        key: 'copyUrl_'+this.$route.params.type,
        icon: 'far fa-clone',
        title: this.t('Copy URL'),
        showCondition: this.$store.state.table.previewLink && this.viewDisallowed,
        click: this.copyUrl
      },
      sendPostbacks: {
        key: 'resend_'+this.$route.params.type,
        icon: 'fas fa-arrow-turn-right',
        title: this.t('Resend'),
        showCondition: this.is('postbacks') && false,
        click: this.resendPostbacks,
        disabled: !selectedItems.length || selectedItems.length > 100,
      },
      preview: {
        key: 'preview_'+this.$route.params.type,
        icon: 'far fa-external-link-square-alt',
        title: this.t('Preview'),
        showCondition: this.$store.state.table.previewLink && this.viewDisallowed,
        click: () => window.open(this.$store.state.table.previewLink, '_blank'),
      },
      excludeHome: {
        key: 'exclude_'+this.$route.params.type,
        icon: 'far fa-page-break',
        title: this.t('Exclude'),
        showCondition: !this.isReport(),
        click: this.handleExcludeStartNewTab,
        disabled: !selectedItems.length,
        planLimits: this.bulkReportPlanLimits,
      },
      exclude: {
        key: 'exclude_'+this.$route.params.type,
        icon: 'far fa-page-break',
        title: this.t('Exclude'),
        showCondition: this.isReport(),
        click: this.handleExclude,
        disabled: !selectedItems.length || this.$store.getters.selectedItem.id === 'Unknown',
        planLimits: this.bulkReportPlanLimits,
      },
      excludeContinueNewTab: {
        key: 'excludeWithNewTab_'+this.$route.params.type,
        icon: 'far fa-page-break',
        title: this.t('Exclude with new tab'),
        showCondition: this.isReport(),
        click: this.handleExcludeWithNewTab,
        disabled: !selectedItems.length || this.$store.getters.selectedItem.id === 'Unknown',
        planLimits: this.bulkReportPlanLimits,
      },
      excludeStartNewTab: {
        key: 'excludeStartNewTab_'+this.$route.params.type,
        icon: 'far fa-page-break',
        title: this.t('Exclude Report with new tab'),
        showCondition: this.isReport(),
        click: this.handleExcludeStartNewTab,
        disabled:  !selectedItems.length || this.$store.getters.selectedItem.id === 'Unknown',
        planLimits: this.bulkReportPlanLimits,
      },
      changeWorkspace: {
        key: 'changeWorkspace_'+this.$route.params.type,
        icon: 'fas fa-window-restore',
        title: this.t('Change Workspace'),
        showCondition: this.isMainTab && this.dialogActionsEvents[this.selectedGroupNormalized]?.changeWorkspace,
        click: this.handleChangeWorkspace,
        disabled: selectedItems.length!==1 || this.isAnySelectedItemsNotElements || !this.isUserOrMemberAdmin
      }
    }
  }

  get itemMenu(): MenuReportRowAction[] {
    return [
      this.itemActions.updateCost,
      this.itemActions.duplicate,
      this.itemActions.archive,
      this.itemActions.restore,
      this.itemActions.copyUrl,
      this.itemActions.sendPostbacks,
      this.itemActions.changeWorkspace,
      this.itemActions.preview,
      this.itemActions.campaignLinks,
      this.itemActions.startStop
    ];
  }

  get selectedGroup(): string {
    return this.$store.state.table.menuOverride || this.$store.state.table.nextReportGroup || this.$route.params.type;
  }

  normalizeGroup(group: string) {
    return camelCase(group);
  }

  get selectedGroupNormalized(): string {
    return this.normalizeGroup(this.selectedGroup);
  }

  get bulkEditPlanLimits(): string | boolean {
    if(!this.isBulkEdit) return false
    switch (this.$route.params.type){
      case 'campaigns':
        return 'bulkEditCampaigns'
      case 'offers':
        return 'bulkEditOffers'
      case 'landings':
        return 'bulkEditLandings'
    }
  }
  
  get creationPlanLimits(): string | boolean {
    switch (this.$route.params.type){
      case 'campaigns':
        return 'campaignsAmount'
      case 'offers':
        return 'offersAmount'
      case 'landings':
        return 'landingsAmount'
      case 'flows':
        return 'flowsAmount'
      case 'affiliate-networks':
        return 'affiliateNetworksAmount'
      case 'traffic-sources':
        return 'trafficSourcesAmount'
    }
  }
  
  get createFromTemplatePlanLimits(): string | boolean {
    switch (this.$route.params.type){
      case 'affiliate-networks':
        return 'affiliateNetworksAmount'
      case 'traffic-sources':
        return 'trafficSourcesAmount'
    }
  }
  
  get startStopButtonText(): string {
    if(this.$store.getters.selectedItem?.['DT_RowIntegrationStatus'] !== 'active') return this.t('Start');
    
    switch (this.$store.getters.selectedItem?.['DT_RowCampaignExternalStatus']){
      case '1':
      case '3':
      case '8':
      case '9':
        return this.t('Start')
      case '2':
      case '4':
      case '6':
      case '7':
        return this.t('Stop')
      default: return ''
    }
  }
  
  get startStopButtonIcon(): string {
    if(this.startStopButtonText===this.t('Start')){
      return 'play'
    }
    if(this.startStopButtonText===this.t('Stop')){
      return 'stop'
    }
  }
  
  get startStopPlanLimits(): string | boolean {
    switch (this.$store.getters.selectedItem?.['DT_RowIntegrationType']){
      case 'propeller':
        return 'integrationPropellerads'
      case 'mgid':
        return 'integrationMgid'
      case 'revcontent':
        return 'integrationRevcontent'
      case 'exoclick':
        return 'integrationExoclick'
      case 'trafficjunky':
        return 'integrationTrafficjunky'
      case 'facebook':
        return 'integrationFacebookConversions'
    }
  }

  get bulkReportPlanLimits(): string | boolean {
    if(this.$store.state.table.selectedItems.length <= 1) return '';
    if(this.isCustomTab) return 'bulkReportCustomParams'
    switch (this.$route.params.type){
      case 'campaigns':
        return 'bulkReportCampaign'
      case 'offers':
        return 'bulkReportOffer'
      case 'landings':
        return 'bulkReportLanding'
      case 'flows':
        return 'bulkReportFlow'
      case 'traffic-sources':
        return 'bulkReportTrafficSource'
      case 'affiliate-networks':
        return 'bulkReportAffiliateNetwork'
    }
  }

  get reportsWithArchiveButton() {
    return ['campaigns', 'offers', 'landings', 'flows', 'traffic-sources', 'affiliate-networks'];
  }

  get archiveDisallowed() {
    return this.reportsWithArchiveButton.indexOf(this.selectedGroup) === -1;
  }

  handleDrilldownForward() {
    if(!this.checkDrildown()) {
      return
    }

    this.bus.emit('freeze-table');

    this.$helpers.afterBrowserRedraw(() => { 
      this.drilldownForward();
      this.bus.emit('reload-report');
    });
  }

  handleDrilldownForwardNewTab() {
    if(!this.checkDrildown()) {
      return
    }

    this.bus.emit('freeze-table');

    this.$helpers.afterBrowserRedraw(() => { 
      this.drilldownForwardNewTab();
      this.bus.emit('reload-report');
    });
  }

  handleExclude() {
    if(!this.checkDrildown()) {
      return
    }

    this.bus.emit('freeze-table');

    this.$helpers.afterBrowserRedraw(() => { 
      this.drilldownForward(null, {
        tableNextReportExclude: true,
        tableNextReportExcludeType: ExcludeTypeEnum.concat      
      });
    });
  }

  handleExcludeWithNewTab() {
    if(!this.checkDrildown()) {
      return
    }
    
    this.bus.emit('freeze-table');

    this.$helpers.afterBrowserRedraw(() => { 
      this.drilldownForwardNewTab(null, {
        tableNextReportExclude: true,
        tableNextReportExcludeType: ExcludeTypeEnum.concat      
      });
      this.bus.emit('reload-report');
    });
  }

  handleExcludeStartNewTab() {
    this.reportExclude();
  }

  handleChangeWorkspace() {
    this.bus.emit('open-dialog', { name: 'ChangeWorkspace' });
  }

  get nextReport() {
    //return this.$store.state.tableNextReport.id !== 'Unknown' && this.$store.state.tableNextReport;
    return this.$store.getters.selectedItem;
  }

  get editDisallowed() {
    return !!(this.nextReport.id && this.aclCanEditItem(this.nextReport));
  }

  get viewDisallowed() {
    return !!(this.nextReport.id && this.aclCanViewItem(this.nextReport));
  }

  get bulkEditDisallowed() {
    const selectedItems = this.$store.state.table.selectedItems.filter(({ isDirect }) => !isDirect)
    const isBulkEditAllowed =  this.dialogActionsEvents[this.selectedGroupNormalized]?.bulkEdit
    if (isBulkEditAllowed && selectedItems.length > 1) {
      if (selectedItems.length > 50) {
        return false
      }

      return selectedItems.every(({ DT_RowId: id, DT_RowWorkspaceId: workspaceId }) =>
        selectedItems[0].DT_RowWorkspaceId === workspaceId && this.aclCanViewItem({ id, workspaceId }))
    }

    return this.viewDisallowed
  }

  get createDisallowed(): boolean {
    return !this.aclCanCreate;
  }

  get duplicateDisallowed(): boolean {
    return !this.aclCanCreateOnSection({ workspaceId: this.$store.getters.selectedItem.workspaceId })
      || !this.editDisallowed
      || !this.nextReport.id;
  }

  toArchive() {
    return this.$store.state.table.selectedItems.filter((item: any) => item.DT_RowStatus !== 'archived' && this.aclCanEditItem({
        id:item.DT_RowId,
        workspaceId:item.DT_RowWorkspaceId
      }));
  }

  toRestore() {
    return this.$store.state.table.selectedItems.filter((e: any) => e.DT_RowStatus === 'archived');
  }

  copyUrl() {
    const url = this.$store.state.table.previewLink;
    const el = document.createElement('textarea');
    el.value = url;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }


  resendPostbacks() {
    this.bus.emit('open-dialog', {
      name: 'ResendPostbacks',
      data: {
        isBeforeSend:true,
      }
    });
  }

  get tutorial() {
    return tutorial(this);
  }
}
